import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { deepEqual } from '../../shared/functions/deepEqual';
import { FileUploadService } from './file-upload.service';
import { FileUploadDataViewModel, UploadedFileViewModel } from './file-upload.view-model';
import { GlobalFileUploadService } from './global-file-upload.service';

@Component({
  selector: 'osn-cxb-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  providers: [
    FileUploadService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileUploadComponent implements ControlValueAccessor, OnDestroy, OnInit {
  @Input() public hasDarkImageBackground = false;
  @Input() public hasBorder = false;
  @Input() public hasDeleteOption = false;
  @Input() public vm: FileUploadDataViewModel;
  @Input() public required: boolean;
  @Input() public isImageAlreadyBelongsToResource: boolean;
  @Input() public readonly = false;
  @Input() public deleteWarningText: string;
  @Input() public shouldUploadPNG = false;
  @Input() public verticalLayout = false;
  @Output() public deleteFile = new EventEmitter<void>();
  @ViewChild('fileInput', { static: true }) private readonly fileInput: ElementRef<HTMLInputElement>;
  @Input('upload-btn-data-testid') uploadButtonTestId = 'upload-image-button';
  @Input('delete-img-data-testid') deleteImageIconTestId = 'delete-image-icon';

  private readonly destroyed$ = new Subject<void>();
  public isDisabled = false;
  public isDragOver = false;
  public value: UploadedFileViewModel;

  public imageLoaded = false;

  public onChange = (_?: UploadedFileViewModel): void => {};
  public onTouched = (): void => {};

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly fileUploadService: FileUploadService,
    private readonly globalFileUploadService: GlobalFileUploadService
  ) {}

  public ngOnInit(): void {
    this.globalFileUploadService.registerFileUploadComponent(this);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.globalFileUploadService.unregisterFileUploadComponent(this);
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  public registerOnChange(fn: (_?: UploadedFileViewModel) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public writeValue(value: UploadedFileViewModel): void {
    if (deepEqual(value, this.value)) {
      console.warn('Skipping write value in file-upload component because it would set the exact same value as already set.');
      return;
    }
    this.value = value;
    this.fileInput.nativeElement.value = '';
    this.imageLoaded = false;
    this.cdr.markForCheck();
  }

  public onFileInputChange(event: Event): void {
    const fileInput: HTMLInputElement = event.target as HTMLInputElement;
    this.uploadFile(fileInput.files);
  }

  public uploadFile(fileList: FileList | null): void {
    if (!fileList || !fileList.length) {
      return;
    }
    this.fileUploadService
      .uploadFile$(fileList[0])
      .pipe(
        takeUntil(this.destroyed$),
        filter((res) => !!res)
      )
      .subscribe((response: UploadedFileViewModel) => {
        this.writeValue(response);
        this.onChange(response);
      });
    this.onTouched();
  }

  public onUploadClick(): void {
    this.fileInput.nativeElement.click();
  }

  public async onDeleteFileClick() {
    this.deleteFile.emit();
    let confirmed = false;
    if (this.isImageAlreadyBelongsToResource) {
      confirmed = await this.fileUploadService.hideFile(this.required, this.deleteWarningText);
    } else {
      confirmed = await this.fileUploadService.deleteFile(this.value.id, this.required, this.deleteWarningText);
    }
    if (confirmed) {
      this.writeValue(null);
      this.onChange(null);
    }
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    if (event?.dataTransfer?.files) {
      this.fileInput.nativeElement.files = event.dataTransfer.files;
      this.uploadFile(this.fileInput.nativeElement.files);
    }
  }

  public previewLoaded() {
    this.imageLoaded = true;
  }

  public get shouldShowPNGLabel(): boolean {
    return this.shouldUploadPNG && !/(.)+(\.[Pp][Nn][Gg])/.test(this.value?.name);
  }
}
